import * as React from "react";
import { Link } from "react-router-dom";
import {
  Clock,
  TrendingUp,
  TrendingDown,
  ArrowRight,
  Calendar,
  Award,
} from "lucide-react";

import { cn } from "../../lib/utils";
import {
  Card,
  CardHeader,
  CardContent,
  CardDescription,
} from "./card";
import { Heading, Text, MonoText } from "./typography";
import { PercentValue, AnimatedValue, CurrencyValue } from "./value-display";
import { Skeleton } from "./skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./tooltip";

/**
 * HoldingAgeInsight Component
 *
 * Analyzes portfolio holdings by age and shows:
 * - Distribution of holdings by time-in-market buckets
 * - Correlation between holding age and performance
 * - Longest-held position highlight
 * - Average holding period
 *
 * Key insight: Time in market is a crucial factor in investment success
 */

// Age buckets for categorization
const AGE_BUCKETS = [
  { id: "0-6m", label: "< 6 months", min: 0, max: 182, color: "bg-blue-500" },
  { id: "6m-1y", label: "6-12 months", min: 182, max: 365, color: "bg-sky-500" },
  { id: "1-2y", label: "1-2 years", min: 365, max: 730, color: "bg-teal-500" },
  { id: "2-5y", label: "2-5 years", min: 730, max: 1825, color: "bg-emerald-500" },
  { id: "5y+", label: "5+ years", min: 1825, max: Infinity, color: "bg-green-600" },
];

/**
 * Calculate the age of a holding in days
 */
function calculateAgeDays(dateAcquired) {
  if (!dateAcquired) return null;
  const acquired = new Date(dateAcquired);
  const now = new Date();
  const diffTime = now.getTime() - acquired.getTime();
  return Math.floor(diffTime / (1000 * 60 * 60 * 24));
}

/**
 * Format age in human-readable format
 */
function formatAge(days) {
  if (days === null || days === undefined) return "Unknown";
  if (days < 30) return `${days} day${days !== 1 ? "s" : ""}`;
  if (days < 365) {
    const months = Math.floor(days / 30);
    return `${months} month${months !== 1 ? "s" : ""}`;
  }
  const years = Math.floor(days / 365);
  const remainingMonths = Math.floor((days % 365) / 30);
  if (remainingMonths === 0) {
    return `${years} year${years !== 1 ? "s" : ""}`;
  }
  return `${years}y ${remainingMonths}m`;
}

/**
 * Analyze holdings and compute age-related metrics
 */
function analyzeHoldingAges(holdings) {
  // Filter to only include investments (not cash, real estate, or liabilities)
  const investments = holdings.filter(
    (h) =>
      !h.isLiability &&
      ["Stock", "ETF", "Cryptocurrency", "Precious Metal"].includes(h.assetType)
  );

  if (investments.length === 0) {
    return null;
  }

  // Calculate age for each holding
  const holdingsWithAge = investments
    .map((h) => ({
      ...h,
      ageDays: calculateAgeDays(h.dateAcquired),
    }))
    .filter((h) => h.ageDays !== null);

  if (holdingsWithAge.length === 0) {
    return null;
  }

  // Bucket distribution
  const bucketStats = AGE_BUCKETS.map((bucket) => {
    const holdingsInBucket = holdingsWithAge.filter(
      (h) => h.ageDays >= bucket.min && h.ageDays < bucket.max
    );
    const count = holdingsInBucket.length;
    const totalValue = holdingsInBucket.reduce(
      (sum, h) => sum + (h.totalValueEUR || 0),
      0
    );
    const avgGainPercent =
      holdingsInBucket.length > 0
        ? holdingsInBucket.reduce((sum, h) => sum + (h.gainPercent || 0), 0) /
          holdingsInBucket.length
        : 0;

    return {
      ...bucket,
      count,
      totalValue,
      avgGainPercent,
      percentage: (count / holdingsWithAge.length) * 100,
    };
  });

  // Calculate overall average holding period
  const totalDays = holdingsWithAge.reduce((sum, h) => sum + h.ageDays, 0);
  const avgDays = totalDays / holdingsWithAge.length;

  // Find longest-held position
  const longestHeld = holdingsWithAge.reduce((longest, h) =>
    h.ageDays > (longest?.ageDays || 0) ? h : longest
  );

  // Calculate weighted average age by value
  const totalValue = holdingsWithAge.reduce(
    (sum, h) => sum + (h.totalValueEUR || 0),
    0
  );
  const weightedAvgDays =
    totalValue > 0
      ? holdingsWithAge.reduce(
          (sum, h) => sum + h.ageDays * (h.totalValueEUR || 0),
          0
        ) / totalValue
      : avgDays;

  // Correlation analysis: compare performance of newer vs older holdings
  const newHoldings = holdingsWithAge.filter((h) => h.ageDays < 365);
  const oldHoldings = holdingsWithAge.filter((h) => h.ageDays >= 365);

  const newAvgGain =
    newHoldings.length > 0
      ? newHoldings.reduce((sum, h) => sum + (h.gainPercent || 0), 0) /
        newHoldings.length
      : null;
  const oldAvgGain =
    oldHoldings.length > 0
      ? oldHoldings.reduce((sum, h) => sum + (h.gainPercent || 0), 0) /
        oldHoldings.length
      : null;

  return {
    totalCount: holdingsWithAge.length,
    bucketStats,
    avgDays,
    weightedAvgDays,
    longestHeld,
    newVsOld: {
      newCount: newHoldings.length,
      oldCount: oldHoldings.length,
      newAvgGain,
      oldAvgGain,
    },
  };
}

/**
 * Progress bar component for bucket visualization
 */
function BucketBar({ bucket, maxCount }) {
  const widthPercent = maxCount > 0 ? (bucket.count / maxCount) * 100 : 0;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="group flex items-center gap-2 py-1 cursor-default">
            <Text size="xs" className="w-20 text-muted-foreground shrink-0">
              {bucket.label}
            </Text>
            <div className="flex-1 h-5 bg-muted/50 rounded-md overflow-hidden relative">
              <div
                className={cn(
                  "h-full rounded-md transition-all duration-500 ease-out",
                  bucket.color
                )}
                style={{ width: `${Math.max(widthPercent, bucket.count > 0 ? 4 : 0)}%` }}
              />
              {bucket.count > 0 && (
                <span className="absolute inset-0 flex items-center justify-center text-xs font-medium text-white mix-blend-difference">
                  {bucket.count}
                </span>
              )}
            </div>
            <div className="w-16 text-right shrink-0">
              <PercentValue
                value={bucket.avgGainPercent}
                size="xs"
                showSign
                showTrend={false}
              />
            </div>
          </div>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs">
          <div className="space-y-1">
            <p className="font-medium">{bucket.label}</p>
            <p className="text-xs text-muted-foreground">
              {bucket.count} holding{bucket.count !== 1 ? "s" : ""} ({bucket.percentage.toFixed(0)}%)
            </p>
            <p className="text-xs">
              Total value:{" "}
              <CurrencyValue value={bucket.totalValue} size="xs" className="inline" />
            </p>
            <p className="text-xs">
              Avg return: <PercentValue value={bucket.avgGainPercent} size="xs" showSign />
            </p>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/**
 * Main HoldingAgeInsight Component
 */
export function HoldingAgeInsight({ holdings, loading, className }) {
  const analysis = React.useMemo(() => {
    if (!holdings || holdings.length === 0) return null;
    return analyzeHoldingAges(holdings);
  }, [holdings]);

  // Find max count for scaling bars
  const maxCount = React.useMemo(() => {
    if (!analysis) return 0;
    return Math.max(...analysis.bucketStats.map((b) => b.count));
  }, [analysis]);

  return (
    <AnimatedValue delay={500} animation="slide-up">
      <Card className={cn("flex flex-col", className)}>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="rounded-md bg-primary/10 p-1.5">
                <Clock className="h-4 w-4 text-primary" />
              </div>
              <div>
                <Heading level={5} className="text-sm">
                  Time in Market
                </Heading>
                <CardDescription className="text-xs">
                  Holding age distribution
                </CardDescription>
              </div>
            </div>
          </div>
        </CardHeader>

        <CardContent className="pt-0 flex-1">
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="flex items-center gap-2">
                  <Skeleton className="h-4 w-20" />
                  <Skeleton className="h-5 flex-1" />
                  <Skeleton className="h-4 w-14" />
                </div>
              ))}
            </div>
          ) : !analysis ? (
            <div className="text-center py-6">
              <Calendar className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
              <Text variant="secondary" size="sm">
                No investment data available
              </Text>
              <Text variant="secondary" size="xs" className="mt-1">
                Add holdings with purchase dates to see insights
              </Text>
            </div>
          ) : (
            <div className="space-y-3">
              {/* Distribution bars */}
              <div className="space-y-0.5">
                {analysis.bucketStats.map((bucket) => (
                  <BucketBar
                    key={bucket.id}
                    bucket={bucket}
                    maxCount={maxCount}
                  />
                ))}
              </div>

              {/* Summary stats */}
              <div className="pt-2 border-t border-border/50 space-y-2">
                {/* Average holding period */}
                <div className="flex items-center justify-between">
                  <Text size="xs" className="text-muted-foreground">
                    Average holding period
                  </Text>
                  <MonoText size="xs" className="font-medium">
                    {formatAge(Math.round(analysis.avgDays))}
                  </MonoText>
                </div>

                {/* Longest held */}
                {analysis.longestHeld && (
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <Award className="h-3.5 w-3.5 text-yellow-500 shrink-0" />
                      <Text
                        size="xs"
                        className="text-muted-foreground truncate"
                        title={analysis.longestHeld.assetName || analysis.longestHeld.name}
                      >
                        {analysis.longestHeld.ticker || analysis.longestHeld.assetName}
                      </Text>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <MonoText size="xs">
                        {formatAge(analysis.longestHeld.ageDays)}
                      </MonoText>
                      <PercentValue
                        value={analysis.longestHeld.gainPercent || 0}
                        size="xs"
                        showSign
                        showTrend={false}
                      />
                    </div>
                  </div>
                )}

                {/* New vs Old comparison */}
                {analysis.newVsOld.newCount > 0 && analysis.newVsOld.oldCount > 0 && (
                  <div className="pt-1 mt-1 border-t border-border/30">
                    <div className="flex items-center gap-2 text-xs">
                      <div className="flex-1 flex items-center justify-between">
                        <span className="text-muted-foreground">
                          {"<1yr"} ({analysis.newVsOld.newCount})
                        </span>
                        <PercentValue
                          value={analysis.newVsOld.newAvgGain}
                          size="xs"
                          showSign
                          showTrend={false}
                        />
                      </div>
                      <span className="text-muted-foreground">vs</span>
                      <div className="flex-1 flex items-center justify-between">
                        <span className="text-muted-foreground">
                          {">1yr"} ({analysis.newVsOld.oldCount})
                        </span>
                        <PercentValue
                          value={analysis.newVsOld.oldAvgGain}
                          size="xs"
                          showSign
                          showTrend={false}
                        />
                      </div>
                    </div>
                    {/* Insight message */}
                    {analysis.newVsOld.oldAvgGain !== null &&
                      analysis.newVsOld.newAvgGain !== null && (
                        <div className="mt-1.5 flex items-center gap-1.5 text-xs">
                          {analysis.newVsOld.oldAvgGain > analysis.newVsOld.newAvgGain ? (
                            <>
                              <TrendingUp className="h-3 w-3 text-success" />
                              <span className="text-muted-foreground">
                                Older holdings outperforming
                              </span>
                            </>
                          ) : analysis.newVsOld.newAvgGain > analysis.newVsOld.oldAvgGain ? (
                            <>
                              <TrendingDown className="h-3 w-3 text-warning" />
                              <span className="text-muted-foreground">
                                Newer holdings leading
                              </span>
                            </>
                          ) : (
                            <span className="text-muted-foreground">
                              Similar performance across ages
                            </span>
                          )}
                        </div>
                      )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Link to investments */}
          {analysis && (
            <Link
              to="/holdings/investments"
              className="mt-3 flex items-center justify-center gap-1.5 text-xs text-muted-foreground hover:text-primary transition-colors"
            >
              View all {analysis.totalCount} investments
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          )}
        </CardContent>
      </Card>
    </AnimatedValue>
  );
}

export default HoldingAgeInsight;
